<!-- BToast.vue -->
<template>
  <div class="toast-container position-fixed top-10 end-5" id="toastPlacement">
    <div
      class="toast"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      :data-bs-autohide="autoHide.toString()"
      :data-bs-delay="delay"
      :id="id"
    >
      <div class="toast-header" v-if="!noHeader">
        <div v-if="icon" class="me-2 text-primary" v-html="icon"></div>
        <strong class="me-auto">{{ title }}</strong>
        <small v-if="subtitle">{{ subtitle }}</small>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="toast"
          aria-label="Close"
        ></button>
      </div>
      <div class="toast-body">
        <slot>{{ body }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import Toast from 'bootstrap/js/dist/toast'

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: 'Notification'
  },
  subtitle: {
    type: String,
    default: ''
  },
  body: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: ''
  },
  variant: {
    type: String,
    default: '',
    validator: (value) =>
      [
        '',
        'primary',
        'secondary',
        'success',
        'danger',
        'warning',
        'info',
        'light',
        'dark'
      ].includes(value)
  },
  autoHide: {
    type: Boolean,
    default: true
  },
  noHeader: {
    type: Boolean,
    default: false
  },
  delay: {
    type: Number,
    default: 5000
  },
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['shown', 'hidden'])

const toastClasses = computed(() => ({
  [`bg-${props.variant}`]: props.variant,
  'text-white': [
    'primary',
    'secondary',
    'success',
    'danger',
    'info',
    'dark'
  ].includes(props.variant)
}))

let toastInstance = null

const initializeToast = () => {
  nextTick(() => {
    const toastElement = document.getElementById(props.id)
    if (toastElement) {
      toastInstance = new Toast(toastElement, {
        autohide: props.autoHide,
        delay: props.delay
      })

      toastElement.addEventListener('shown.bs.toast', () => emit('shown'))
      toastElement.addEventListener('hidden.bs.toast', () => emit('hidden'))

      if (props.visible) {
        toastInstance.show()
      }
    } else {
      console.warn(`Toast element with id "${props.id}" not found.`)
    }
  })
}

onMounted(initializeToast)

watch(
  () => props.visible,
  (newValue) => {
    if (toastInstance) {
      if (newValue) {
        toastInstance.show()
      } else {
        toastInstance.hide()
      }
    } else {
      console.warn('Toast instance not initialized.')
    }
  }
)

onUnmounted(() => {
  if (toastInstance) {
    toastInstance.dispose()
  }
})
</script>
